"use client";

import { useEffect, useState } from "react";

import { BlogSearch } from "@/src/components/feat/blog/BlogSearch";
import { CardList } from "@/src/components/feat/blog/CardList";
import { Hero } from "@/src/components/feat/blog/Hero";
import { NotFoundMessage } from "@/src/components/feat/blog/NotFoundMessage";
import type { Post } from "@/src/lib/blog/posts";

function matchesQuery(post: Post, tokens: string[]) {
  const haystack = [
    post.title,
    post.excerpt,
    post.category,
    post.authorName,
    ...post.tags,
  ]
    .join(" ")
    .toLowerCase();
  return tokens.every((token) => haystack.includes(token));
}

const DEBOUNCE_MS = 300;

interface BlogListingProps {
  posts: Post[];
}

// Owns the search query so the Hero's search box (top right) can filter the
// card grid below it. Every whitespace-separated term must match somewhere in
// a post's title, excerpt, category, author or tags.
export function BlogListing({ posts }: BlogListingProps) {
  // `query` tracks the input on every keystroke; `appliedQuery` trails it by
  // DEBOUNCE_MS and is what actually filters, so the grid doesn't re-render
  // per key. Clearing the box applies immediately.
  const [query, setQuery] = useState("");
  const [appliedQuery, setAppliedQuery] = useState("");

  useEffect(() => {
    if (query.trim() === "") {
      setAppliedQuery("");
      return;
    }
    const id = setTimeout(() => setAppliedQuery(query), DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [query]);

  const tokens = appliedQuery.toLowerCase().replace(/#/g, " ").split(/\s+/).filter(Boolean);
  const results = tokens.length > 0 ? posts.filter((p) => matchesQuery(p, tokens)) : posts;
  const noResults = tokens.length > 0 && results.length === 0;

  return (
    <>
      <Hero search={<BlogSearch value={query} onChange={setQuery} />} />

      {noResults ? (
        <NotFoundMessage
          title="No blogs found"
          description={`We couldn't find any posts matching "${appliedQuery.trim()}". Try a different keyword, or clear the search to see everything.`}
          ctaLabel="Clear search"
          onCtaClick={() => setQuery("")}
          headingLevel="h2"
          className="!pt-0"
        />
      ) : (
        // Keyed by query so "Load more" pagination resets on each new search.
        <CardList key={tokens.join(" ")} posts={results} />
      )}
    </>
  );
}
